import { Diagnostic } from '@codemirror/lint';
import { memoize } from 'lodash-es';

import {
  queryAssistanceClient,
  VerifyResponse,
} from '@dynatrace-sdk/client-query';

import { DQLEditorValidityInfo } from '../dql-editor-types.js';

const severities: { [key: string]: 'error' | 'warning' | 'info' } = {
  error: 'error',
  warning: 'warning',
  info: 'info',
};

const toSeverity = (severity: string): 'error' | 'warning' | 'info' =>
  severities[severity.toLowerCase()] || 'info';

const toLinterMessage = ({ notifications }: VerifyResponse): Diagnostic[] =>
  (notifications ?? [])
    .map((notification): Diagnostic | undefined =>
      notification.syntaxPosition && notification.severity
        ? {
            from: notification.syntaxPosition.start.index,
            to: notification.syntaxPosition.end.index + 1,
            message: notification.message ?? '',
            severity: toSeverity(notification.severity),
          }
        : undefined,
    )
    .filter((x): x is Diagnostic => x !== undefined);

const transformToValidityInfo = (
  diagnostics: Diagnostic[],
): DQLEditorValidityInfo[] =>
  diagnostics.map(
    (diagnostic: Diagnostic): DQLEditorValidityInfo => ({
      ...diagnostic,
      severity: diagnostic.severity === 'hint' ? 'info' : diagnostic.severity,
    }),
  );

const rawVerify = async (query: string) => {
  return await queryAssistanceClient.queryVerify({ body: { query } });
};

// time after which validity information about a certain query is evicted from the cache
const TTL_MS = 3 * 1_000;
const memoizedRawVerify = memoize(rawVerify);

/**
 * Tracks active TTL timers for each cached query.
 * Key: query string
 * Value: timeout handle for cache invalidation
 */
const ttlTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Validates the syntax of the given DQL query.
 *
 * @remarks Validation results are cached for a short time-to-live (TTL) to reduce redundant requests.
 * After the TTL expires, the cache entry and its associated timer are cleared.
 *
 * @internal
 */
export const verifyQuery = async (
  query: string,
): Promise<VerifyResponse | undefined> => {
  try {
    // early exit if query is empty
    if (!query.length) {
      return undefined;
    }

    const result = await memoizedRawVerify(query);

    // reset pending timer
    const pendingTimeout = ttlTimers.get(query);
    if (pendingTimeout) {
      clearTimeout(pendingTimeout);
    }

    // start new timer with fixed TTL
    const timeout = setTimeout(() => {
      // clear cache and timer for query
      memoizedRawVerify.cache.delete(query);
      ttlTimers.delete(query);
    }, TTL_MS);
    ttlTimers.set(query, timeout);

    return result;
  } catch {
    // on error, clear cache and any pending timer for this query
    memoizedRawVerify.cache.delete(query);
    const timeout = ttlTimers.get(query);
    if (timeout) {
      clearTimeout(timeout);
      ttlTimers.delete(query);
    }
  }
  return undefined;
};

/**
 * Validates the syntax of the given DQL query.
 * @param content - the DQL query to validate
 * @param onValidationStart - called before the validation starts
 * @param onValidationEnd - called after validation finished
 * @internal
 */
export const dqlLinter = async (
  content: string,
  onValidationStart?: () => void,
  onValidationEnd?: (validityInfo: DQLEditorValidityInfo[]) => void,
): Promise<DQLEditorValidityInfo[]> => {
  onValidationStart?.();
  const result = await verifyQuery(content);

  const dqlErrors = result ? toLinterMessage(result) : [];
  const dqlEditorValidityInfos = transformToValidityInfo(dqlErrors);
  onValidationEnd?.(dqlEditorValidityInfos);

  return dqlEditorValidityInfos;
};
